import { useMemo } from "react"
import { getContentSetDuplicateMetricIds } from "helpers/metrics"
import { getTemplateMetricIds, getViewDuplicateMetricIds, type TemplateMetricIdsOptions } from "connected-components/asset-view/helpers/get-template-metric-ids"
import { getSummaryDuplicateMetricIds, type SummaryDuplicateMetricIdsOptions } from "connected-components/asset-view/templates/summary/get-summary-duplicate-metric-ids"
import {
  useSourceDisplayedMetricIds,
  type CompositionVisibilityOptions,
  type SourceMetricIdsChangeHandler,
} from "./use-source-displayed-metric-ids"

interface SourceDisplayedMetricsParams {
  flattenedMetrics?: Record<string, unknown> | null
  onMetricsChange?: SourceMetricIdsChangeHandler
  compositionVisibility?: CompositionVisibilityOptions
}

interface UseSourceTemplateMetricIdsParams extends SourceDisplayedMetricsParams {
  metricOptions: TemplateMetricIdsOptions
  includeSummaryRows?: boolean
}

export function useSourceTemplateMetricIds({
  flattenedMetrics,
  onMetricsChange,
  metricOptions,
  includeSummaryRows = false,
  compositionVisibility,
}: UseSourceTemplateMetricIdsParams) {
  const metricIds = useMemo(() => {
    if (!flattenedMetrics) return []
    return includeSummaryRows
      ? getViewDuplicateMetricIds(flattenedMetrics, metricOptions)
      : getTemplateMetricIds(flattenedMetrics, metricOptions)
  }, [flattenedMetrics, metricOptions, includeSummaryRows])

  useSourceDisplayedMetricIds({
    metricIds,
    flattenedMetrics,
    onMetricsChange,
    includeDefaultCompositionMetrics: true,
    compositionVisibility,
  })
}

interface UseSourceContentSetMetricIdsParams extends SourceDisplayedMetricsParams {
  contentSetId: string
  selectedCategory: string
  selectedMetricDisplay: string
}

export function useSourceContentSetMetricIds({
  contentSetId,
  flattenedMetrics,
  selectedCategory,
  selectedMetricDisplay,
  onMetricsChange,
  compositionVisibility,
}: UseSourceContentSetMetricIdsParams) {
  const metricIds = useMemo(
    () => getContentSetDuplicateMetricIds(
      contentSetId,
      flattenedMetrics,
      selectedCategory,
      selectedMetricDisplay,
    ),
    [contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay],
  )

  useSourceDisplayedMetricIds({
    metricIds,
    flattenedMetrics,
    onMetricsChange,
    includeDefaultCompositionMetrics: true,
    compositionVisibility,
  })
}

interface UseSourceSummaryMetricIdsParams extends SourceDisplayedMetricsParams {
  summaryOptions: SummaryDuplicateMetricIdsOptions
}

export function useSourceSummaryMetricIds({
  flattenedMetrics,
  onMetricsChange,
  summaryOptions,
  compositionVisibility,
}: UseSourceSummaryMetricIdsParams) {
  const metricIds = useMemo(
    () => getSummaryDuplicateMetricIds(flattenedMetrics, summaryOptions),
    [flattenedMetrics, summaryOptions],
  )

  useSourceDisplayedMetricIds({
    metricIds,
    flattenedMetrics,
    onMetricsChange,
    includeDefaultCompositionMetrics: true,
    compositionVisibility,
  })
}
